/**
 * /email-templates/:clientId/:docType — Editor de plantilla email.
 *
 * Layout:
 *   ├── Header: nav back + título + acciones (Preview / Restore / Save)
 *   ├── Tabs (Subject / HTML / Text) — cada uno con su Monaco apropiado
 *   └── Preview panel (HTML iframe + Subject + Text colapsable)
 *
 * Cada save genera version+1. Restore default hace DELETE → vuelve al
 * default Taxor.
 */

import { lazy, Suspense, useEffect, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { ArrowLeft, Eye, RotateCcw, Save } from 'lucide-react'
import { toast } from 'sonner'

import { PdfPreview } from '../pdf-templates/components/pdf-preview'
import {
  useDeleteEmailTemplate,
  useEmailTemplate,
  useUpsertEmailTemplate,
} from '@/hooks/api/use-email-templates'
import {
  DOC_TYPES,
  DOC_TYPE_LABELS,
  emailTemplatesApi,
  type DocType,
  type EmailPreview,
} from '@/lib/api-email-templates'

const Monaco = lazy(() =>
  import('@monaco-editor/react').then((m) => ({ default: m.default })),
)

type Tab = 'subject' | 'html' | 'text'

export function EmailTemplateEditorPage() {
  const { clientId, docType } = useParams<{ clientId: string; docType: string }>()

  const isValidDocType = !!docType && (DOC_TYPES as readonly string[]).includes(docType)
  if (!clientId || !isValidDocType) {
    return <Navigate to="/email-templates" replace />
  }
  const dt = docType as DocType

  const tplQ = useEmailTemplate(clientId, dt)
  const upsert = useUpsertEmailTemplate()
  const del = useDeleteEmailTemplate()

  const [subject, setSubject] = useState('')
  const [html, setHtml] = useState('')
  const [text, setText] = useState('')
  const [tab, setTab] = useState<Tab>('subject')

  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [preview, setPreview] = useState<EmailPreview | null>(null)
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null)

  useEffect(() => {
    if (tplQ.data && subject === '' && html === '' && text === '') {
      setSubject(tplQ.data.subject_template)
      setHtml(tplQ.data.html_template)
      setText(tplQ.data.text_template)
    }
  }, [tplQ.data, subject, html, text])

  const isCustom = tplQ.data?.source === 'db'
  const dirty =
    subject !== (tplQ.data?.subject_template ?? '') ||
    html !== (tplQ.data?.html_template ?? '') ||
    text !== (tplQ.data?.text_template ?? '')

  const handleGeneratePreview = async () => {
    setPreviewLoading(true)
    setPreviewError(null)
    try {
      const r = await emailTemplatesApi.preview(clientId, dt, {
        subject_template: subject,
        html_template: html,
        text_template: text,
      })
      setPreview(r)
      // Convertir el HTML rendered a Blob para reusar PdfPreview iframe.
      const blob = new Blob([r.html], { type: 'text/html;charset=utf-8' })
      setPreviewBlob(blob)
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string }
      setPreviewError(`No se pudo renderizar: ${err?.response?.data?.message || err?.message || 'error'}`)
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleSave = () => {
    upsert.mutate(
      {
        clientId,
        docType: dt,
        subject_template: subject,
        html_template: html,
        text_template: text,
      },
      {
        onSuccess: () => toast.success(`Versión guardada (v${(tplQ.data?.version ?? 0) + 1})`),
      },
    )
  }

  const handleRestore = () => {
    if (
      !confirm(
        `¿Restaurar el default Taxor para "${DOC_TYPE_LABELS[dt]}"? Se perderá la plantilla custom.`,
      )
    ) {
      return
    }
    del.mutate({ clientId, docType: dt })
  }

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <Link
            to="/email-templates"
            className="mb-2 inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg-primary"
          >
            <ArrowLeft className="h-3 w-3" />
            Volver a plantillas email
          </Link>
          <h1 className="text-2xl font-bold text-fg-primary">
            Email — {DOC_TYPE_LABELS[dt]}
            {isCustom ? (
              <span className="ml-3 align-middle text-xs font-medium uppercase tracking-wider text-brand-700">
                custom v{tplQ.data?.version}
              </span>
            ) : (
              <span className="ml-3 align-middle text-xs font-medium uppercase tracking-wider text-fg-muted">
                default Taxor
              </span>
            )}
          </h1>
          <p className="mt-1 text-xs text-fg-muted">
            Tax-Client: <code className="font-mono">{clientId}</code>
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleGeneratePreview}
            disabled={previewLoading || (!subject && !html && !text)}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-fg-secondary hover:bg-bg-elevated hover:text-fg-primary disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Eye className="h-4 w-4" />
            Generar preview
          </button>
          {isCustom && (
            <button
              type="button"
              onClick={handleRestore}
              disabled={del.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-fg-secondary hover:bg-bg-elevated hover:text-fg-primary disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RotateCcw className="h-4 w-4" />
              Restaurar default
            </button>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || upsert.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Save className="h-4 w-4" />
            {upsert.isPending ? 'Guardando…' : dirty ? 'Guardar cambios' : 'Sin cambios'}
          </button>
        </div>
      </div>

      {tplQ.isLoading && (
        <p className="text-center text-sm text-fg-muted">Cargando plantilla…</p>
      )}
      {tplQ.isError && (
        <p className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          No se pudo cargar la plantilla: {queryErrorMessage(tplQ.error)}
        </p>
      )}

      {tplQ.data && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Editor con tabs */}
          <div>
            <div className="mb-2 flex items-center gap-2">
              <TabBtn current={tab} value="subject" onClick={setTab}>
                Subject
              </TabBtn>
              <TabBtn current={tab} value="html" onClick={setTab}>
                HTML
              </TabBtn>
              <TabBtn current={tab} value="text" onClick={setTab}>
                Text
              </TabBtn>
            </div>
            <Suspense
              fallback={
                <div className="flex h-[70vh] items-center justify-center rounded-md border border-border bg-bg-elevated text-sm text-fg-muted">
                  Cargando editor…
                </div>
              }
            >
              <div className="overflow-hidden rounded-md border border-border">
                {tab === 'subject' && (
                  <Monaco
                    height="70vh"
                    defaultLanguage="plaintext"
                    value={subject}
                    onChange={(v) => setSubject(v ?? '')}
                    theme="vs-dark"
                    options={editorOpts}
                  />
                )}
                {tab === 'html' && (
                  <Monaco
                    height="70vh"
                    defaultLanguage="html"
                    value={html}
                    onChange={(v) => setHtml(v ?? '')}
                    theme="vs-dark"
                    options={editorOpts}
                  />
                )}
                {tab === 'text' && (
                  <Monaco
                    height="70vh"
                    defaultLanguage="plaintext"
                    value={text}
                    onChange={(v) => setText(v ?? '')}
                    theme="vs-dark"
                    options={editorOpts}
                  />
                )}
              </div>
            </Suspense>
          </div>

          {/* Preview */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-medium uppercase tracking-wider text-fg-muted">
                Preview email (datos sample)
              </p>
              {preview && (
                <p className="truncate text-xs text-fg-secondary">
                  <span className="font-semibold">Subject:</span> {preview.subject}
                </p>
              )}
            </div>
            <PdfPreview
              blob={previewBlob}
              loading={previewLoading}
              error={previewError}
            />
            {preview && (
              <details className="mt-3 rounded-md border border-border bg-bg-surface p-3">
                <summary className="cursor-pointer text-xs font-medium text-fg-secondary">
                  Ver versión texto plano (fallback)
                </summary>
                <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap text-xs text-fg-secondary">
                  {preview.text}
                </pre>
              </details>
            )}
          </div>
        </div>
      )}

      <div className="mt-6 rounded-md border border-border bg-bg-surface p-4 text-xs text-fg-secondary">
        <p className="mb-1 font-semibold text-fg-primary">
          Variables disponibles en los 3 sub-templates:
        </p>
        <code className="block whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-fg-muted">
          .DocType .DocType.HumanLabel .DocNumber .IssueDate .IssueTime .CUFE .QRCodeURL{'\n'}
          .Issuer.NIT .Issuer.LegalName .Issuer.Email .Issuer.Phone .Issuer.City ...{'\n'}
          .Buyer.NIT .Buyer.LegalName .Buyer.Email .Buyer.City ...{'\n'}
          .Subtotal .TaxIVA .GrandTotal .Currency .PaymentMeans .DueDate{'\n'}
          .ReferencedFENumber .ReferencedFEDate .DiscrepancyReason (NC/ND){'\n'}
          .XMLFilename .PDFFilename{'\n\n'}
          Helpers: formatCOP, formatDate, formatDateTime, upper, join
        </code>
      </div>
    </div>
  )
}

/** Mensaje legible (español si el backend lo da) desde un error de query. */
function queryErrorMessage(err: unknown): string {
  const e = err as { response?: { data?: { message?: string } }; message?: string }
  return e?.response?.data?.message || e?.message || 'error desconocido'
}

const editorOpts = {
  minimap: { enabled: false },
  fontSize: 13,
  lineNumbers: 'on' as const,
  wordWrap: 'on' as const,
  scrollBeyondLastLine: false,
  tabSize: 2,
  renderWhitespace: 'selection' as const,
  automaticLayout: true,
}

function TabBtn({
  current,
  value,
  onClick,
  children,
}: {
  current: Tab
  value: Tab
  onClick: (v: Tab) => void
  children: React.ReactNode
}) {
  const active = current === value
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? 'bg-brand-50 text-brand-700'
          : 'text-fg-secondary hover:bg-bg-elevated hover:text-fg-primary'
      }`}
    >
      {children}
    </button>
  )
}
